// Usage Store
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import type { UsageLog, UsageLogCreate } from '@/types/usage'
import { usageApi } from '@/api/usageApi'
import { useEquipmentStore } from '@/stores/equipmentStore'

export const useUsageStore = defineStore('usage', () => {
  const usageLogs = ref<UsageLog[]>([])
  const currentUsageLog = ref<UsageLog | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const fetchUsageLogs = async () => {
    loading.value = true;
    error.value = null;
    try {
      const res = await usageApi.getAll()
      // API may return a paginated object or an array. Normalize to array.
      if (Array.isArray(res)) {
        usageLogs.value = res
      } else if (res && typeof res === 'object') {
        const r = res as unknown as { results?: UsageLog[] }
        if (Array.isArray(r.results)) {
          usageLogs.value = r.results
        } else {
          usageLogs.value = []
        }
      } else {
        // fallback: wrap single object into array or empty
        usageLogs.value = res ? [res as UsageLog] : []
      }
      // Enrich logs with equipment objects when available
      try {
        const equipmentStore = useEquipmentStore()
        if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
          // try to load equipment to enable enrichment
          await equipmentStore.fetchEquipment()
        }
        usageLogs.value = usageLogs.value.map((l) => {
          const lObj = l as unknown as Record<string, unknown>
          let eqId: number | undefined
          const rawEq = lObj['equipment']

          if (typeof rawEq === 'number') {
            eqId = rawEq
          } else if (rawEq && typeof rawEq === 'object' && 'id' in rawEq) {
            eqId = Number((rawEq as Record<string, unknown>).id)
          }

          if (!eqId && 'equipment_id' in lObj) {
            eqId = Number(lObj['equipment_id'])
          }

          if (eqId) {
            const foundEq = equipmentStore.equipment.find((e) => e.id === eqId)
            const finalEq = foundEq || (typeof rawEq === 'object' ? rawEq : undefined)

            // Explicitly set equipment and equipment_id
            return {
              ...l,
              equipment_id: eqId,
              equipment: finalEq
            } as UsageLog
          }
          return l
        })
      } catch {
        // non-fatal: if equipment store not available, skip enrichment
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch usage logs'
    } finally {
      loading.value = false
    }
  }

  // Re-enrich logs whenever equipment list changes
  try {
    const equipmentStore = useEquipmentStore()
    watch(
      () => equipmentStore.equipment,
      (newList) => {
        if (!Array.isArray(newList) || newList.length === 0) return
        usageLogs.value = usageLogs.value.map((l) => {
          const lObj = l as unknown as Record<string, unknown>
          let eqId: number | undefined
          const rawEq = lObj['equipment']

          if (typeof rawEq === 'number') {
            eqId = rawEq
          } else if (rawEq && typeof rawEq === 'object' && 'id' in rawEq) {
            eqId = Number((rawEq as Record<string, unknown>).id)
          }

          if (!eqId && 'equipment_id' in lObj) {
            eqId = Number(lObj['equipment_id'])
          }

          if (eqId) {
            const foundEq = newList.find((e) => e.id === eqId)
            // If we found a better object in the new list, use it. 
            // Otherwise keep what we had if it was an object.
            const finalEq = foundEq || (typeof rawEq === 'object' ? rawEq : undefined)

            return {
              ...l,
              equipment_id: eqId,
              equipment: finalEq
            } as UsageLog
          }
          return l
        })
      },
      { deep: true },
    )
  } catch {
    // ignore if equipment store isn't available in this environment
  }

  const createUsageLog = async (data: UsageLogCreate | Partial<UsageLog>) => {
    loading.value = true
    error.value = null
    try {
      const newLog = await usageApi.create(data)
      // ensure usageLogs is an array
      if (!Array.isArray(usageLogs.value)) usageLogs.value = []

      const forcedLog = newLog as unknown as Record<string, unknown>
      // resolve eq ID from response or input
      let eqId: number | undefined = undefined
      if (typeof forcedLog['equipment'] === 'number') eqId = forcedLog['equipment']
      if (!eqId && forcedLog['equipment_id']) eqId = Number(forcedLog['equipment_id'])
      // fallback to input data if available and not in response
      if (!eqId && 'equipment_id' in data) eqId = Number((data as any).equipment_id)

      let finalEq: any = undefined
      try {
        const equipmentStore = useEquipmentStore()
        if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
          await equipmentStore.fetchEquipment()
        }
        if (eqId) {
          finalEq = equipmentStore.equipment.find(e => e.id === eqId)
        }
      } catch {
        // ignore
      }

      // If we still don't have finalEq, but response had an object, use that
      if (!finalEq && typeof forcedLog['equipment'] === 'object') {
        finalEq = forcedLog['equipment']
      }

      usageLogs.value.push({
        ...(newLog as UsageLog),
        equipment_id: eqId,
        equipment: finalEq
      } as UsageLog)

      return newLog
    } catch (err) {
      // Log backend validation body for easier debugging in browser console
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e: any = err
      if (e && e.response) {
        // useful to see status and response JSON
        // not exposing to users directly here, but store error message for UI
        // and keep the raw response visible in console

        console.error('Usage create failed', e.response.status, e.response.data)
        if (e.response.data && typeof e.response.data === 'object') {
          // try to build a friendly message from response fields
          const msgs: string[] = []
          for (const k in e.response.data) {
            const v = e.response.data[k]
            if (Array.isArray(v)) msgs.push(`${k}: ${String(v[0])}`)
            else msgs.push(`${k}: ${String(v)}`)
          }
          error.value = msgs.join('; ')
        } else {
          error.value = e.response.data || e.message || 'Failed to create usage log'
        }
      } else {
        error.value = err instanceof Error ? err.message : 'Failed to create usage log'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateUsageLog = async (id: number, data: UsageLogCreate | Partial<UsageLog>) => {
    loading.value = true
    error.value = null
    try {
      const updatedLog = await usageApi.update(id, data)
      const index = usageLogs.value.findIndex((log) => log.id === id)
      if (index !== -1) {
        // try to enrich updatedLog with equipment
        try {
          const equipmentStore = useEquipmentStore()
          if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
            await equipmentStore.fetchEquipment()
          }
          const eq = (equipmentStore.equipment || []).find(
            (e) => Number(e.id) === Number((updatedLog as UsageLog).equipment_id),
          )
          usageLogs.value[index] = { ...(updatedLog as UsageLog), equipment: eq }
        } catch {
          usageLogs.value[index] = updatedLog
        }
      }
      return updatedLog
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update usage log'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteUsageLog = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      await usageApi.delete(id)
      usageLogs.value = usageLogs.value.filter((log) => log.id !== id)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete usage log'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    usageLogs,
    currentUsageLog,
    loading,
    error,
    fetchUsageLogs,
    createUsageLog,
    updateUsageLog,
    deleteUsageLog,
  }
})
